import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CopyButton from 'comps/copy-button';

import {
  editableFieldsFor,
  FIELD_HINTS,
  FIELD_LABELS,
  STATUS,
  publicPageUrl,
  TIER_CHOICES,
  statusLabel,
  statusTone,
} from './constants';
import { dummyPhoto, rowPhotoUrl } from './photo';
import {
  displayName as rowDisplayName,
  parentNote,
  tutorEvidence,
  tutorNote,
} from './row-fields';
import { canPublish } from './queue-fields';
import {
  Body,
  Btn,
  ColLabel,
  CloseBtn,
  EvidenceLink,
  EvidenceThumb,
  FieldsCol,
  Field,
  FieldRow,
  Hint,
  Input,
  Msg,
  Muted,
  NarrativeTextarea,
  Panel2,
  PanelFoot,
  PanelHead,
  PanelHeadActions,
  PanelHeadTitle,
  PanelSplit,
  PhotoPicker,
  PhotoZoomBtn,
  PhotoZoomScrim,
  Pill,
  PreviewCard,
  PreviewName,
  PreviewNarrative,
  PreviewPhoto,
  PreviewScore,
  PreviewCol,
  PublicPageLink,
  Scrim,
  NoteBlock,
  Spacer,
  TierDot,
  TierFields,
  TierLabel,
  TierOption,
  Two,
  ZoomedPhoto,
} from './styled-comps';

// Copyable, because every one of these is looked up by pasting it somewhere else. A
// missing id says why it is missing rather than showing a blank: for most of them "absent"
// is a fact about the row, not an error.
const idRow = (label, value, copyMessage, absent) => (
  <FieldRow>
    <ColLabel>{label}</ColLabel>
    {value ? (
      <CopyButton text={String(value)} copyMessage={copyMessage} padding="4px 8px" />
    ) : (
      <Muted>{absent}</Muted>
    )}
  </FieldRow>
);

// The record is what gets published, so it leads: the card sits beside the fields that
// change it rather than above them, and the actions that publish or withdraw it sit in the
// footer, one glance from the thing they act on.
const ReviewPanel = ({
  row,
  examName,
  isDirty,
  saving,
  uploadingPhoto,
  photoError,
  onFieldChange,
  onNarrativeChange,
  onPickPhoto,
  onSave,
  onPublish,
  onUnpublish,
  onRemove,
  onClose,
  triage,
  text,
  hasCard,
}) => {
  // Whether a card actually exists, passed in rather than read off `row.record`: for a row
  // with none, that now holds a working copy of the copy a moderator is drafting, and
  // reading it here offered the card editor on rows whose card was never built.
  const hasRecord = hasCard;
  const record = row.record || {};
  const submission = row.submission || {};
  const narrative = record.narrative || [];
  // Shared with the queue, or the drawer contradicts the row that opened it.
  const displayName = rowDisplayName(row) || 'Unnamed record';
  // The queue shows the exam's readable name, so the panel must not fall back to its key.
  const displayExam = record.test || examName || submission.examKey;
  const displayScore = record.score || submission.score;
  // The column is the reliable one: `submission.studentId` is absent on a row an admin
  // built from the form, and ingest refuses a submission without one either way.
  const studentId = row.student_id || submission.studentId;
  const note = tutorNote(row);
  const evidence = tutorEvidence(row);
  // The signed url outlives the panel by only a few minutes, so a stale one is expected
  // rather than exceptional: say so instead of showing a broken-image icon.
  const [evidenceUnreachable, setEvidenceUnreachable] = React.useState(false);
  const evidenceStamp =
    evidence && evidence.uploadedAt
      ? new Date(evidence.uploadedAt).toLocaleDateString()
      : null;
  // The drawer is normally closed between records, but it is not guaranteed to be: without
  // this, one expired link would leave the next record's evidence reading as expired too.
  // Layout, not passive: an effect runs after paint, so switching records without unmounting
  // would show the previous record's "link has expired" for one frame before clearing.
  React.useLayoutEffect(() => {
    setEvidenceUnreachable(false);
  }, [row.id]);
  const fromParent = parentNote(row);
  const teacherName = record.currentTeacher || submission.tutorName;
  // Both are read more than once below — the img src, its onError fallback, and the
  // placeholder hint — so resolve each once per render.
  const photoUrl = rowPhotoUrl(row);
  const fallbackPhoto = dummyPhoto(displayName);
  // Not `hasRecord`: the service attaches a photo to any row that still exists —
  // `attach_photo` writes `meta.photo` whether or not a card has been assembled, and
  // `to_dict` serves it either way, so an unassembled row can own one. Only a withdrawn
  // row is excluded, because `/remove/` purges its images and re-uploading would put a
  // photo back on an achievement the parent withdrew.
  const canAttachPhoto = row.status !== STATUS.REMOVED;
  // `status_rules.frozen_reason` states this once on the service, for triage and for the
  // record PATCH alike: a published row must be unpublished before it changes, and a
  // withdrawn one is finished. Stated once here for the same reason — two copies drift.
  const isFrozen = row.status === STATUS.REMOVED || row.status === STATUS.PUBLISHED;
  // The same set `triage_rules` accepts, so the drawer cannot offer a decision the
  // endpoint will refuse.
  const canTriage = !isFrozen;
  // And the same set `record_edit_blocker` accepts. Without this the drawer offered a Save
  // on a live card that the endpoint answers with a 400.
  const canEditRecord = !isFrozen;
  // Re-tiering an already-judged row is a different act from judging a new one.
  const applyLabel = row.tier ? 'Re-tier record' : 'Apply decision';
  const [zoomed, setZoomed] = useState(false);

  // Escape has to collapse the photo without also closing the drawer under it. The
  // drawer's handler is a bubble-phase listener on `document`, so this capture-phase one
  // runs first and `stopImmediatePropagation` keeps the event from reaching it.
  useEffect(() => {
    if (!zoomed) return undefined;
    const onKey = e => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      setZoomed(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [zoomed]);

  // One node, wrapped or not, so the zoomable and plain cases cannot drift apart.
  const photoNode = (
    <PreviewPhoto
      src={photoUrl || fallbackPhoto}
      alt={`${displayName}'s photo`}
      onError={e => {
        e.target.src = fallbackPhoto;
      }}
    />
  );

  return (
    <>
      <Scrim onClick={onClose} />
      <Panel2 role="dialog" aria-modal="true" aria-label={`Review ${displayName}`}>
        <PanelHead>
          <PanelHeadTitle>Review &mdash; {displayName}</PanelHeadTitle>
          <PanelHeadActions>
            {isDirty && <Pill tone="review">Unsaved changes</Pill>}
            <CloseBtn onClick={onClose} aria-label="Close">
              &times;
            </CloseBtn>
          </PanelHeadActions>
        </PanelHead>

        <PanelSplit>
          <PreviewCol>
            <ColLabel>{hasRecord ? 'What gets published' : 'Submitted'}</ColLabel>
            <PreviewCard>
              {/* `rowPhotoUrl` now screens out the seeded bare filenames, but onError still
                  earns its place: a re-hosted URL 404s once `purge` deletes its object, and
                  without this that shows a broken-image icon instead of the dummy. */}
              {photoUrl ? (
                <PhotoZoomBtn
                  type="button"
                  title="Expand photo"
                  aria-label={`Expand ${displayName}'s photo`}
                  onClick={() => setZoomed(true)}
                >
                  {photoNode}
                </PhotoZoomBtn>
              ) : (
                photoNode
              )}
              <PreviewName>{displayName}</PreviewName>
              {record.catLabel && <Pill tone="category">{record.catLabel}</Pill>}
              <PreviewScore>
                <b>{displayExam}</b>
                {displayScore ? ` — ${displayScore}` : ''}
              </PreviewScore>
              {narrative.map((para, i) => (
                <PreviewNarrative key={i}>{para}</PreviewNarrative>
              ))}
            </PreviewCard>

            {canAttachPhoto && (
              <>
                <PhotoPicker disabled={uploadingPhoto}>
                  {uploadingPhoto ? 'Uploading…' : 'Replace photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    disabled={uploadingPhoto}
                    onChange={e => onPickPhoto(e.target.files && e.target.files[0], e)}
                  />
                </PhotoPicker>
                <Hint>JPG or PNG, up to 5 MB — downscaled before upload</Hint>
              </>
            )}
            {canAttachPhoto && !photoUrl && !photoError && (
              <Hint>
                Showing a placeholder — no re-hosted photo yet
                {record.photo ? ` (the record says “${record.photo}”)` : ''}
              </Hint>
            )}
            {photoError && <Msg error>{photoError}</Msg>}
          </PreviewCol>

          <FieldsCol>
            <Two>
              <FieldRow>
                <ColLabel>Status</ColLabel>
                <div>
                  <Pill tone={statusTone(row.status)}>{statusLabel(row.status)}</Pill>
                </div>
              </FieldRow>
              <FieldRow>
                <ColLabel>Parent consent</ColLabel>
                <div>
                  <Pill tone={row.consent ? 'granted' : 'denied'}>
                    {row.consent ? 'Granted' : 'Not granted'}
                  </Pill>
                </div>
              </FieldRow>
            </Two>

            {row.status === STATUS.PARKED && row.parked_reason && (
              <Msg error>{row.parked_reason}</Msg>
            )}

            {/* The tutor's one typed sentence arrives as both the exam name and the
                result, so it reaches the teacher app, the parent app and the card twice
                over — a typo here is a typo everywhere. Cleaning it up sits above the
                Decision section: fix the words before judging them. */}
            {text.canEdit && (
              <FieldRow>
                <ColLabel>Achievement text</ColLabel>
                <Hint>
                  {text.canEditName
                    ? 'The tutor typed one line, and it arrived as both the name and the result. Split it and fix any spelling before deciding.'
                    : 'The exam name comes from the library and is not editable here. Correct the result if the tutor mistyped it.'}
                </Hint>
                {text.canEditName && (
                  <>
                    <Field htmlFor="hof-text-name">Exam name</Field>
                    <Input
                      id="hof-text-name"
                      value={text.examName}
                      maxLength={text.maxExamName}
                      onChange={e => text.setExamName(e.target.value)}
                    />
                    {text.nameTooLong && (
                      <Msg error>
                        {text.nameLength} characters — an exam name is at most{' '}
                        {text.maxExamName}. This is the sentence, not the name of the exam.
                      </Msg>
                    )}
                  </>
                )}
                <Field htmlFor="hof-text-result">Result</Field>
                <Input
                  id="hof-text-result"
                  value={text.result}
                  maxLength={text.maxResult}
                  onChange={e => text.setResult(e.target.value)}
                />
                {/* The row arrived longer than the field now accepts, so Save is off with
                    nothing to click. Name the cap and what it costs, or it reads as the
                    drawer being broken. */}
                {text.resultTooLong && (
                  <Msg error>
                    {text.resultLength} characters — a certificate prints at most{' '}
                    {text.maxResult}, and one longer than that is never issued. Keep the
                    result alone, not the whole sentence.
                  </Msg>
                )}

                {/* The disagreement that parks every parked row on prod, and the only one
                    nothing could answer until now. The library's type is the only value
                    the endpoint accepts, because settling means agreeing with it — if the
                    library is the wrong side, the library is what to edit. */}
                {text.disputed && (
                  <>
                    <Hint>
                      The tutor recorded this as a <strong>{text.declaredType}</strong>{' '}
                      result, but the library lists this exam as{' '}
                      <strong>{text.settledType}</strong>. Nothing can judge it until that
                      is settled.
                    </Hint>
                    <label htmlFor="hof-settle-type">
                      <input
                        id="hof-settle-type"
                        type="checkbox"
                        checked={text.settling}
                        onChange={e => text.setSettling(e.target.checked)}
                      />{' '}
                      Record it as the library does — {text.settledType}
                    </label>
                    {/* Settling alone is not enough: each scoring type is read from a
                        different field, and one left blank falls back to whatever the
                        tutor put in the result — so a letter-graded exam would publish
                        the raw mark. */}
                    {text.settling && text.valueMoves && (
                      <>
                        <Field htmlFor="hof-settle-value">{text.valueLabel}</Field>
                        <Input
                          id="hof-settle-value"
                          value={text.value}
                          maxLength={text.maxResult}
                          onChange={e => text.setValue(e.target.value)}
                        />
                        {text.valueHint && <Hint>{text.valueHint}</Hint>}
                      </>
                    )}
                  </>
                )}

                <Btn
                  variant="primary"
                  disabled={!text.isDirty || !text.isValid || text.saving}
                  onClick={text.save}
                >
                  {text.saving ? 'Saving…' : 'Save text'}
                </Btn>
              </FieldRow>
            )}

            {/* The decision the whole publication flow branches on, and the only one
                nothing else in the product can make. Offered on any row a verdict can
                still change: a published row must be unpublished first and a withdrawn one
                is finished, which is exactly what the service refuses. */}
            {canTriage && (
              <FieldRow>
                <ColLabel>Decision</ColLabel>
                <Hint>
                  {row.tier
                    ? 'Choosing again re-tiers this record.'
                    : 'Nobody has judged this yet. Choosing is what moves it out of this queue.'}
                </Hint>
                {TIER_CHOICES.map(choice => {
                  const selected = triage.tier === choice.tier;
                  const current = !triage.tier && row.tier === choice.tier;
                  return (
                    <React.Fragment key={choice.tier}>
                      <TierOption
                        type="button"
                        tone={choice.tone}
                        selected={selected}
                        aria-pressed={selected}
                        onClick={() => triage.setTier(choice.tier)}
                      >
                        <TierDot tone={choice.tone} selected={selected}>
                          <span />
                        </TierDot>
                        <div>
                          <TierLabel>
                            {choice.label}
                            {current && <Pill>Current</Pill>}
                          </TierLabel>
                          <Hint>{choice.note}</Hint>
                        </div>
                      </TierOption>
                      {selected && triage.explainerRequired && (
                        <TierFields tone={choice.tone}>
                          <FieldRow>
                            <Field htmlFor="hof-triage-explainer">
                              Explainer for this achievement
                            </Field>
                            <NarrativeTextarea
                              id="hof-triage-explainer"
                              value={triage.explainer}
                              onChange={e => triage.setExplainer(e.target.value)}
                              placeholder="What this achievement is, and what winning it means."
                            />
                            <Hint>
                              Stored on this record, not on the shared &ldquo;Other / Not
                              listed&rdquo; entry &mdash; so the next custom achievement does
                              not inherit these words.
                            </Hint>
                          </FieldRow>
                          <FieldRow>
                            <Field htmlFor="hof-triage-category">Category label</Field>
                            <Input
                              id="hof-triage-category"
                              value={triage.category}
                              onChange={e => triage.setCategory(e.target.value)}
                              placeholder="Competition"
                            />
                            <Hint>Optional. Shown as the chip on the published card.</Hint>
                          </FieldRow>
                        </TierFields>
                      )}
                    </React.Fragment>
                  );
                })}
                <Two>
                  <Btn
                    variant="primary"
                    disabled={!triage.isDirty || triage.explainerMissing || triage.saving}
                    onClick={triage.save}
                  >
                    {triage.saving ? 'Applying\u2026' : applyLabel}
                  </Btn>
                  {triage.explainerMissing ? (
                    <Msg error>
                      A custom achievement needs explainer copy before it can be a Hall of
                      Fame achievement.
                    </Msg>
                  ) : (
                    <Muted>
                      {triage.isDirty
                        ? 'Writes the tier and stamps the review trail'
                        : 'Pick one of the three'}
                    </Muted>
                  )}
                </Two>
              </FieldRow>
            )}

            {/* The only words on this screen that are the tutor's own. It explains the
                achievement to whoever reviews it and is never published, so it sits above
                the card copy rather than among the fields that become the card. */}
            {note && (
              <FieldRow>
                <ColLabel>Tutor&rsquo;s note</ColLabel>
                <NoteBlock>{note}</NoteBlock>
                <Hint>From the submission — not published on the card</Hint>
              </FieldRow>
            )}

            {/* Beside the parent's note because it is the same act: what they sent to
                make the claim reviewable. Never card copy, never the child's photo. */}
            {evidence && (
              <FieldRow>
                <ColLabel>Tutor&rsquo;s evidence</ColLabel>
                {evidence.url && !evidenceUnreachable ? (
                  <EvidenceLink
                    href={evidence.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open full size"
                  >
                    <EvidenceThumb
                      src={evidence.url}
                      alt="Scorecard or certificate the tutor attached"
                      onError={() => setEvidenceUnreachable(true)}
                    />
                  </EvidenceLink>
                ) : (
                  <Msg error>
                    {evidence.url
                      ? 'That link has expired — reopen this record for a new one.'
                      : 'Could not sign this for viewing. The file is still stored.'}
                  </Msg>
                )}
                <Hint>
                  {evidenceStamp
                    ? `Attached by the tutor on ${evidenceStamp} — never published`
                    : 'Attached by the tutor — never published'}
                </Hint>
              </FieldRow>
            )}

            {/* The parent's own words, sent with their consent. Beside the tutor's rather
                than under the consent chip: the two are the same kind of thing, and a
                moderator reads them together to decide. */}
            {fromParent && (
              <FieldRow>
                <ColLabel>Parent&rsquo;s note</ColLabel>
                <NoteBlock>{fromParent}</NoteBlock>
                <Hint>From the parent&rsquo;s consent — not published on the card</Hint>
              </FieldRow>
            )}

            {/* No card yet, which on these queues is the normal state rather than a fault:
                assembly parks on missing consent, and a row waits here until it arrives.
                The story is still writable — it is held on the record and the card picks
                it up when one is finally built. */}
            {!hasRecord && (
              <FieldRow>
                <ColLabel>Story</ColLabel>
                <Hint>
                  No card has been assembled yet
                  {row.consent ? '' : ', because this record is still waiting on consent'}.
                  Anything written here is held on the record, and the card takes it up
                  when this is judged a Hall of Fame achievement.
                </Hint>
              </FieldRow>
            )}

            {hasRecord &&
              editableFieldsFor(row.tier).map(f => (
                <FieldRow key={f}>
                  <Field htmlFor={`hof-${f}`}>{FIELD_LABELS[f] || f}</Field>
                  <Input
                    id={`hof-${f}`}
                    value={(row.record && row.record[f]) || ''}
                    onChange={e => onFieldChange(f, e.target.value)}
                  />
                  {FIELD_HINTS[f] && <Hint>{FIELD_HINTS[f]}</Hint>}
                </FieldRow>
              ))}

            {narrative.map((para, i) => (
              <FieldRow key={i}>
                <Field htmlFor={`hof-narrative-${i}`}>
                  Narrative — paragraph {i + 1} of {narrative.length}
                </Field>
                <NarrativeTextarea
                  id={`hof-narrative-${i}`}
                  value={para}
                  onChange={e => onNarrativeChange(i, e.target.value)}
                />
              </FieldRow>
            ))}

            {/* The slug is this student's public URL, so it is labelled as one rather than
                shown as an opaque machine string. Parked rows have none yet.

                The absolute URL, not the bare path this used to copy: that path was also
                missing the `/student/` segment, so pasting it into a browser 404'd. Only
                offered as a live link once the row is PUBLISHED -- a draft holds a slug but
                `tutor_view.public_url` answers None for it, and there is no page to open. */}
            {row.slug && (
              <FieldRow>
                <ColLabel>Public URL</ColLabel>
                <CopyButton
                  text={publicPageUrl(row.slug)}
                  copyMessage="Copy public URL"
                  padding="4px 8px"
                />
                {row.status === STATUS.PUBLISHED ? (
                  <PublicPageLink
                    href={publicPageUrl(row.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open the live page ↗
                  </PublicPageLink>
                ) : (
                  <Hint>This page goes live when the record is published.</Hint>
                )}
              </FieldRow>
            )}
            {/* Who this record is about, and who filed it. `logged_by` is deliberately not
                folded into the teacher: the ingest scope admits staff, so on a row an admin
                filed, the two name different people. */}
            {idRow(
              'Student ID',
              studentId,
              'Copy student ID',
              'No student id on this record',
            )}
            <FieldRow>
              <ColLabel>Teacher</ColLabel>
              <div>
                {teacherName && <Body>{teacherName}</Body>}
                {row.teacher_id ? (
                  <CopyButton
                    text={String(row.teacher_id)}
                    copyMessage="Copy teacher ID"
                    padding="4px 8px"
                  />
                ) : (
                  <Muted>
                    {teacherName
                      ? 'Name only — the App sent no teacher id on rows this old'
                      : 'No teacher on this record'}
                  </Muted>
                )}
              </div>
            </FieldRow>
            {idRow(
              'Logged by',
              row.logged_by,
              'Copy logged-by ID',
              'Logged by a service key, which names nobody',
            )}
            <FieldRow>
              <ColLabel>Record ID</ColLabel>
              <CopyButton text={row.id} copyMessage="Copy record ID" padding="4px 8px" />
            </FieldRow>
          </FieldsCol>
        </PanelSplit>

        <PanelFoot>
          <Btn
            variant="primary"
            disabled={saving || !isDirty || !canEditRecord}
            onClick={onSave}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Btn>
          {canPublish(row) && (
            <Btn variant="publish" onClick={onPublish}>
              Publish
            </Btn>
          )}
          {row.status === STATUS.PUBLISHED && <Btn onClick={onUnpublish}>Unpublish</Btn>}
          {/* Why Save is off, in the endpoint's own terms. "No changes yet" would say
              nothing about the reason it can never become available on this row. */}
          {!canEditRecord && (
            <Muted>
              {row.status === STATUS.PUBLISHED
                ? 'Unpublish this record before editing its card'
                : 'This record has been withdrawn'}
            </Muted>
          )}
          {canEditRecord && !isDirty && <Muted>No changes yet</Muted>}
          <Spacer />
          {row.status !== STATUS.REMOVED && (
            <Btn variant="danger" onClick={onRemove}>
              Remove
            </Btn>
          )}
        </PanelFoot>
      </Panel2>

      {zoomed && (
        <PhotoZoomScrim onClick={() => setZoomed(false)}>
          <ZoomedPhoto
            src={photoUrl}
            alt={`${displayName}'s photo, expanded`}
            onError={e => {
              e.target.src = fallbackPhoto;
            }}
          />
        </PhotoZoomScrim>
      )}
    </>
  );
};

ReviewPanel.propTypes = {
  row: PropTypes.shape({}).isRequired,
  examName: PropTypes.string,
  isDirty: PropTypes.bool,
  saving: PropTypes.bool,
  uploadingPhoto: PropTypes.bool,
  photoError: PropTypes.string,
  onFieldChange: PropTypes.func.isRequired,
  onNarrativeChange: PropTypes.func.isRequired,
  onPickPhoto: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onUnpublish: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  triage: PropTypes.shape({}).isRequired,
  text: PropTypes.shape({}).isRequired,
  hasCard: PropTypes.bool,
};

export default ReviewPanel;
